// src/hooks/useScrollToSection.ts
import { useCallback, useEffect, useState } from "react";

export type ScrollBehaviorStrict = "smooth" | "auto";

export interface ScrollOptions {
    offset?: number;
    duration?: number;
    behavior?: ScrollBehaviorStrict;
    block?: ScrollLogicalPosition;
    inline?: ScrollLogicalPosition;
    easeFunction?: (t: number) => number;
  }

const defaultEase = (t: number) => t * (2 - t); // easeOutQuad

export const useScrollToSection = (stickyHeaderId?: string) => {
  const getStickyOffset = () => {
    const sticky = stickyHeaderId ? document.getElementById(stickyHeaderId) : null;
    return sticky ? sticky.offsetHeight : 0;
  };

  const scrollTo = useCallback(
    (idOrElement: string | HTMLElement | null, options?: ScrollOptions) => {
      let element: HTMLElement | null = null;

      if (typeof idOrElement === "string") {
        element = document.getElementById(idOrElement);
      } else if (idOrElement instanceof HTMLElement) {
        element = idOrElement;
      }

      if (element) {
        const offset = (options?.offset ?? 0) + getStickyOffset();
        const duration = options?.duration ?? 600;
        const ease = options?.easeFunction ?? defaultEase;

        const start = window.scrollY;
        const end = element.getBoundingClientRect().top + window.scrollY - offset;
        const change = end - start;
        const startTime = performance.now();

        const animateScroll = (currentTime: number) => {
          const time = Math.min(1, (currentTime - startTime) / duration);
          const easedTime = ease(time);
          window.scrollTo(0, start + change * easedTime);

          if (time < 1) {
            requestAnimationFrame(animateScroll);
          }
        };

        requestAnimationFrame(animateScroll);

        element.focus?.({ preventScroll: true });
      }
    },
    [stickyHeaderId]
  );

  return scrollTo;
};

// --- ScrollSpy Hook ---
export const useScrollSpy = (sectionIds: string[], offset: number = 0): string | null => {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      let current: string | null = null;
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top;
          if (top - offset < window.innerHeight / 2) {
            current = id;
          }
        }
      }
      setActiveId(current);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sectionIds, offset]);

  return activeId;
};
